import { AxiosError } from "axios";
import { useCallback } from "react";
import { SWRResponse } from "swr";
import {
  GenericErrorResponse,
  GetAccount200Response,
} from "../../codegen-api";
import { authApi } from "../../services/api/apiFetcher";

// Cohesive `/account` mutation callbacks extracted verbatim from the provider.
// Called unconditionally once at the provider's top level; the four
// `useCallback`s keep their original relative order and dependency arrays.
export function useAccountMutations(
  fetcher: ReturnType<typeof authApi>,
  swr: SWRResponse<GetAccount200Response, Error>
) {
  const removeApiKey = useCallback(
    async (key: string) => {
      try {
        await (
          await fetcher.deleteApiKey({ api_key: key })
        )();
        swr.mutate();
      } catch (error) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error removing API key"
        );
      }
    },
    [fetcher, swr]
  );

  const removeSigningKey = useCallback(
    async (key: string) => {
      try {
        await (
          await fetcher.deleteSigningKey({ signing_key: key })
        )();
        const mutatedData = swr.data
          ? {
              ...swr.data,
              signing_keys: (swr.data.signing_keys || []).filter(
                (k) => k.signing_key !== key
              ),
            }
          : undefined;
        swr.mutate(mutatedData, {
          revalidate: false,
        });
      } catch (error) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error removing API key"
        );
      }
    },
    [fetcher, swr]
  );

  const addApiKey = useCallback(
    async (name: string, read_only: boolean = false) => {
      try {
        await (
          await fetcher.postApiKey({ name, read_only })
        )();
        swr.mutate();
      } catch (error) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error adding API key"
        );
      }
    },
    [fetcher, swr]
  );

  const updateIsolatedMargin = useCallback(
    async (instrumentId: string, margin: string) => {
      try {
        await (
          await fetcher.postAccountUpdateMargin({
            instrument: instrumentId,
            isolated_margin: margin,
          })
        )();
        swr.mutate();
      } catch (error) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error adding API key"
        );
      }
    },
    [fetcher, swr]
  );

  return {
    removeApiKey,
    removeSigningKey,
    addApiKey,
    updateIsolatedMargin,
  };
}
